
import Base from '@/shared/core/components/Base.vue';
import { SnackType } from '@/shared/core/events';
import { Actions } from '@/shared/core/store/actions';
import { Getters } from '@/shared/core/store/getters';
import { Mutations } from '@/shared/core/store/mutations';
import {
  ValidationRule,
  NotEmptyRule,
  NotValidEmailRule,
} from '@/shared/core/validation/validation.rules';
import Component from 'vue-class-component';
import BookingConfigurationClosedDates from '../components/BookingConfigurationClosedDates.vue';
import BookingConfigurationSlots from '../components/BookingConfigurationSlots.vue';
import { DefaultWorkingDays } from '../components/bookings.const';
import { BookingConfiguration } from '../model/BookingConfiguration';
import { ClosedDate } from '../model/ClosedDate';
import { Slot } from '../model/Slot';
import { WorkingDay } from '../model/WorkingDay';
import { bookingsService } from '../services/bookings.service';

@Component({
  components: { BookingConfigurationSlots, BookingConfigurationClosedDates },
})
export default class ConfigBooking extends Base {
  locationDescription: string | undefined;

  get bookingConfiguration(): BookingConfiguration | null {
    return this.$store.getters[`booking/${Getters.BOOKING_CONFIGURATION}`];
  }

  get notEmailRules(): ValidationRule[] {
    return [NotEmptyRule, NotValidEmailRule];
  }

  get notBookingDaysRule(): ValidationRule[] {
    return [NotEmptyRule];
  }

  form: {
    email: string | null | undefined;
    customerId: string;
    workingDays: number[];
    closedDates: ClosedDate[];
    bookableDays: number;
    slots: Slot[];
    locationDescription: string;
  } = {
    email: '',
    customerId: '',
    workingDays: [],
    closedDates: [],
    bookableDays: 0,
    slots: [],
    locationDescription: '',
  };

  valid = false;

  workingDaysSelected = [];

  deleteConfirmation = false;

  mounted() {
    this.initForm();
  }

  initForm() {
    if (this.bookingConfiguration) {
      this.form = {
        email: this.bookingConfiguration.email,
        customerId: this.bookingConfiguration.customerId,
        bookableDays: this.bookingConfiguration.bookableDays,
        slots: this.bookingConfiguration.slots,
        closedDates: this.bookingConfiguration.closedDates,
        workingDays: this.initWorkingDays(this.bookingConfiguration.workingDays),
        locationDescription: this.bookingConfiguration.locationDescription || '',
      };
    }
    if (!this.bookingConfiguration && this.site) {
      this.form = {
        email: '',
        customerId: this.site.customerId,
        bookableDays: 0,
        slots: [],
        closedDates: [],
        workingDays: [],
        locationDescription: '',
      };
    }
  }

  initWorkingDays(workingDays: WorkingDay[]): number[] {
    return workingDays
      .filter((workingDay) => workingDay.enable)
      .map((workingDay) => workingDay.weekDay);
  }

  formatWorkingDays(days: number[]): WorkingDay[] {
    return DefaultWorkingDays.map((workingDay) => ({
      weekDay: workingDay.weekDay,
      enable: days.some((day) => day === workingDay.weekDay),
    }));
  }

  addSlot() {
    this.form.slots.push({
      disabled: false,
      startTime: '',
      endTime: '',
      seats: 0,
      seatsThreshold: 0,
    });
  }

  deleteSlot(slot: Slot) {
    this.form.slots = this.form.slots.filter(
      (slotItem) =>
        !(
          slotItem.startTime === slot.startTime &&
          slotItem.endTime === slot.endTime &&
          slotItem.seats === slot.seats &&
          slotItem.seatsThreshold === slot.seatsThreshold &&
          slotItem.disabled === slot.disabled
        ),
    );
  }

  addClosedDate() {
    const newDate = new Date();
    newDate.setHours(0, 0, 0, 0);
    this.form.closedDates.push({
      from: newDate.toISOString(),
      to: newDate.toISOString(),
      closedMessage: '',
    });
  }

  deleteClosedDate(closedDate: ClosedDate) {
    this.form.closedDates = this.form.closedDates.filter(
      (closedDateItem) =>
        !(closedDateItem.from === closedDate.from && closedDateItem.to === closedDate.to),
    );
  }

  async sendBookingConfiguration() {
    this.startLoading();
    if (this.site && this.form.email) {
      const bookingConfiguration: BookingConfiguration = {
        customerId: this.site.customerId,
        email: this.form.email,
        bookableDays: this.form.bookableDays,
        slots: this.form.slots,
        workingDays: this.formatWorkingDays(this.form.workingDays),
        closedDates: this.form.closedDates.map((closedDate) => ({
          from: new Date(closedDate.from).toISOString(),
          to: new Date(closedDate.to).toISOString(),
          closedMessage: closedDate.closedMessage || undefined,
        })),
        locationDescription: this.form.locationDescription,
      };

      if (this.bookingConfiguration) {
        await bookingsService.updateBookingConfiguration(bookingConfiguration).then(
          async () => {
            this.snack(`Configuration modifiée avec succès`);
            this.$store.dispatch(
              `booking/${Actions.SET_BOOKING_CONFIGURATION}`,
              this.site?.customerId,
              { root: true },
            );
          },
          (e) => {
            this.snack(
              e.response?.data?.message ||
                `Erreur pendant la sauvegarde de vos modifications (${e.response?.status})`,
              SnackType.ERROR,
            );
          },
        );
      } else {
        await bookingsService.createBookingConfiguration(bookingConfiguration).then(
          async () => {
            this.snack(`Configuration créée avec succès`);
            this.$store.dispatch(
              `booking/${Actions.SET_BOOKING_CONFIGURATION}`,
              this.site?.customerId,
              { root: true },
            );
          },
          (e) => {
            this.snack(
              e.response?.data?.message ||
                `Erreur pendant la sauvegarde de vos modifications (${e.response?.status})`,
              SnackType.ERROR,
            );
          },
        );
      }
      this.releaseLoading();
    }
  }

  async deleteBookingConfiguration() {
    this.startLoading();
    if (this.bookingConfiguration) {
      await bookingsService.deleteBookingConfiguration(this.bookingConfiguration.customerId).then(
        async () => {
          this.snack(`Configuration supprimée avec succès`);
          this.$store.commit(`booking/${Mutations.SET_BOOKING_CONFIGURATION}`, null, {
            root: true,
          });
          if (this.$refs.form) {
            const form = this.$refs.form as HTMLFormElement;
            form.reset();
          }
          this.deleteConfirmation = false;
        },
        (e) => {
          this.snack(
            e.response?.data?.message ||
              `Erreur pendant la suppression de la configuration (${e.response?.status})`,
            SnackType.ERROR,
          );
        },
      );
    }
    this.releaseLoading();
  }

  resetBookingConfiguration() {
    this.initForm();
  }
}
